import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Box, Button, TextField, Typography, Checkbox } from '@mui/material';
import { styled } from '@mui/system';
import usePostData from '../hooks/usePostData';
import FormManager from '../hoc/FormManager';
import Divider from '@mui/material/Divider';
import rtlPlugin from "stylis-plugin-rtl";
import RTL from '../utils/RTL';
import { useAuth } from '../context/AuthContext';
import StyledButton from '../components/CustomButton'
import api from '../config/axiosConfig';
import logo from '../assets/logo.png';

// Styled components
const PageBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  backgroundColor: theme.palette.background.neutral,
}));

const InnerBox = styled(Box)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.background.default,
  minHeight: '60vh',
  minWidth: '25%',
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '1.2rem',
  textAlign: 'center',
  marginBottom: theme.spacing(2),
}));

const StyledForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  '& > :not(style)': {
    marginBottom: theme.spacing(2),
    width: '100%',
  },
}));


const AccentLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.primary.main,
  fontWeight: 500,
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const StyledLogo = styled('img')(({ theme }) => ({
  width: '11rem',
  maxWidth: '60%',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
}));
// Styled Flex Container
const FlexContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  margin: theme.spacing(1, 0),
}));


const getTransparentFieldSx = (value) => (theme) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: value ? 'transparent !important' : undefined,
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: value ? 'transparent !important' : undefined,
    },
    '&.Mui-focused': {
      backgroundColor: 'transparent !important',
    },
  },
  '& .MuiInputBase-input': {
    backgroundColor: value ? 'transparent !important' : undefined,
    color: theme.palette.text.primary,
    WebkitTextFillColor: `${theme.palette.text.primary} !important`,
  },
  '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
    transition: 'background-color 9999s ease-out 0s',
    WebkitTextFillColor: `${theme.palette.text.primary} !important`,
    WebkitBoxShadow: '0 0 0 100px transparent inset',
    backgroundColor: 'transparent !important',
  },
});

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [form, setForm] = React.useState({ email: '', password: '', institute_name: '' });
  const [submitting, setSubmitting] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr(null);

    const { email, password, institute_name } = form;
    if (!email || !password || !institute_name) {
      setErr('ایمیل، پسورد و نام آموزشگاه الزامی است.');
      return;
    }

    try {
      setSubmitting(true);
      // httpOnly cookie will be set by the server
      const res = await api.post('/accounts/api/v1/login/', { email, password, institute_name });
      if (res.data?.success && res.data?.user) {
        await login(res.data.user);        // prime context (roles are derived inside)
        // Redirect to dashboard after successful login
        const from = location.state?.from?.pathname || '/dashboard';
        navigate(from, { replace: true });
      } else {
        setErr(res.data?.error || 'ورود ناموفق بود');
      }
    } catch (e) {
      setErr(e?.response?.data?.error || 'ورود ناموفق بود');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RTL>
      <PageBox>
        <InnerBox>
          <StyledLogo src={logo} alt="برنا" />
          <StyledTypography variant="h3">خوش آمدید!</StyledTypography>
          <StyledForm noValidate onSubmit={handleSubmit}>
            <TextField label="نام آموزشگاه" name="institute_name" value={form.institute_name} onChange={handleChange} />
            <TextField
              label="ایمیل"
              name="email"
              value={form.email}
              onChange={handleChange}
              sx={getTransparentFieldSx(form.email)}
            />
            <TextField
              label="پسورد"
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              sx={getTransparentFieldSx(form.password)}
            />
            {err && <Typography color="error" sx={{ mb: 1 }}>{err}</Typography>}
            <StyledButton type="submit" disabled={submitting}>{submitting ? 'ورود…' : 'ورود'}</StyledButton>
          </StyledForm>
          <Typography variant="body2" sx={{ mb: 2, alignSelf: 'flex-start' }}>
            <AccentLink to="/password-reset">رمز عبور خود را فراموش کرده‌اید؟</AccentLink>
          </Typography>
          <Divider sx={{ width: '100%' }} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            <AccentLink to="/signup">آیا حساب کاربری ندارید؟</AccentLink>
          </Typography>
        </InnerBox>
      </PageBox>
    </RTL>
  );
}